import { 
  Client, 
  Supplier, 
  Appointment, 
  StorefrontProfile,
  InventoryBatch,
  StockMovement,
  ClinicalPatient,
  ClinicalConsultation,
  ClinicalVaccination,
  ClinicalExamVault
} from '../types';
import { ProfileFinancialState } from '../components/ProfileEditorModal';
import { PipelineItem } from '../components/WorkPipelineKanban';
import { UserPlanTier } from '../components/PlanUpgradeModal';

const STORAGE_KEYS = {
  CLIENTS: 'rmax_erp_clients',
  SUPPLIERS: 'rmax_erp_suppliers',
  APPOINTMENTS: 'rmax_erp_appointments',
  KANBAN: 'rmax_erp_kanban',
  FINANCIAL: 'rmax_erp_financial_state',
  PROFILE: 'rmax_erp_storefront_profile',
  PLAN_TIER: 'rmax_erp_plan_tier',
  INVENTORY_BATCHES: 'rmax_erp_inventory_batches',
  STOCK_MOVEMENTS: 'rmax_erp_stock_movements',
  PATIENTS: 'rmax_erp_clinical_patients',
  CONSULTATIONS: 'rmax_erp_clinical_consultations',
  VACCINATIONS: 'rmax_erp_clinical_vaccinations',
  EXAMS: 'rmax_erp_clinical_exams'
};

function safeGet<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch (error) {
    console.error(`Erro ao carregar chave ${key} do localStorage:`, error);
    return fallback;
  }
}

function safeSet<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Erro ao salvar chave ${key} no localStorage:`, error);
  }
}

// ──────────────────────────────────────────────────────────────────────────
// CLIENTES
// ──────────────────────────────────────────────────────────────────────────
export const loadErpClients = (defaultClients: Client[]): Client[] => {
  return safeGet<Client[]>(STORAGE_KEYS.CLIENTS, defaultClients);
};

export const saveErpClients = (clients: Client[]): void => {
  safeSet(STORAGE_KEYS.CLIENTS, clients);
};

// ──────────────────────────────────────────────────────────────────────────
// FORNECEDORES
// ──────────────────────────────────────────────────────────────────────────
export const loadErpSuppliers = (defaultSuppliers: Supplier[]): Supplier[] => {
  return safeGet<Supplier[]>(STORAGE_KEYS.SUPPLIERS, defaultSuppliers);
};

export const saveErpSuppliers = (suppliers: Supplier[]): void => {
  safeSet(STORAGE_KEYS.SUPPLIERS, suppliers);
};

// ──────────────────────────────────────────────────────────────────────────
// AGENDAMENTOS
// ──────────────────────────────────────────────────────────────────────────
export const loadErpAppointments = (defaultAppointments: Appointment[]): Appointment[] => {
  return safeGet<Appointment[]>(STORAGE_KEYS.APPOINTMENTS, defaultAppointments);
};

export const saveErpAppointments = (appointments: Appointment[]): void => {
  safeSet(STORAGE_KEYS.APPOINTMENTS, appointments);
};

// ──────────────────────────────────────────────────────────────────────────
// KANBAN / GESTÃO
// ──────────────────────────────────────────────────────────────────────────
export const loadErpKanban = (defaultItems: PipelineItem[]): PipelineItem[] => {
  return safeGet<PipelineItem[]>(STORAGE_KEYS.KANBAN, defaultItems);
};

export const saveErpKanban = (items: PipelineItem[]): void => {
  safeSet(STORAGE_KEYS.KANBAN, items);
};

// ──────────────────────────────────────────────────────────────────────────
// ESTADO FINANCEIRO & VHB
// ──────────────────────────────────────────────────────────────────────────
export const loadErpFinancialState = (defaultState: ProfileFinancialState): ProfileFinancialState => {
  return safeGet<ProfileFinancialState>(STORAGE_KEYS.FINANCIAL, defaultState);
};

export const saveErpFinancialState = (state: ProfileFinancialState): void => {
  safeSet(STORAGE_KEYS.FINANCIAL, state);
};

// ──────────────────────────────────────────────────────────────────────────
// PERFIL / VITRINE
// ──────────────────────────────────────────────────────────────────────────
export const loadErpProfile = (defaultProfile: StorefrontProfile): StorefrontProfile => {
  return safeGet<StorefrontProfile>(STORAGE_KEYS.PROFILE, defaultProfile);
};

export const saveErpProfile = (profile: StorefrontProfile): void => {
  safeSet(STORAGE_KEYS.PROFILE, profile);
};

// ──────────────────────────────────────────────────────────────────────────
// PLANO COMERCIAL
// ──────────────────────────────────────────────────────────────────────────
export const loadErpPlanTier = (defaultTier: UserPlanTier): UserPlanTier => {
  return safeGet<UserPlanTier>(STORAGE_KEYS.PLAN_TIER, defaultTier);
};

export const saveErpPlanTier = (tier: UserPlanTier): void => {
  safeSet(STORAGE_KEYS.PLAN_TIER, tier);
};

// ──────────────────────────────────────────────────────────────────────────
// ESTOQUE & LOTES
// ──────────────────────────────────────────────────────────────────────────
export const loadErpInventoryBatches = (defaultBatches: InventoryBatch[] = []): InventoryBatch[] => {
  return safeGet<InventoryBatch[]>(STORAGE_KEYS.INVENTORY_BATCHES, defaultBatches);
};

export const saveErpInventoryBatches = (batches: InventoryBatch[]): void => {
  safeSet(STORAGE_KEYS.INVENTORY_BATCHES, batches);
};

export const loadErpStockMovements = (defaultMovements: StockMovement[] = []): StockMovement[] => {
  return safeGet<StockMovement[]>(STORAGE_KEYS.STOCK_MOVEMENTS, defaultMovements);
};

export const saveErpStockMovements = (movements: StockMovement[]): void => {
  safeSet(STORAGE_KEYS.STOCK_MOVEMENTS, movements);
};

// ──────────────────────────────────────────────────────────────────────────
// CLÍNICA: PACIENTES, CONSULTAS, VACINAS & EXAMES
// ──────────────────────────────────────────────────────────────────────────
export const loadErpClinicalPatients = (defaultPatients: ClinicalPatient[] = []): ClinicalPatient[] => {
  return safeGet<ClinicalPatient[]>(STORAGE_KEYS.PATIENTS, defaultPatients);
};

export const saveErpClinicalPatients = (patients: ClinicalPatient[]): void => {
  safeSet(STORAGE_KEYS.PATIENTS, patients);
};

export const loadErpClinicalConsultations = (defaultConsultations: ClinicalConsultation[] = []): ClinicalConsultation[] => {
  return safeGet<ClinicalConsultation[]>(STORAGE_KEYS.CONSULTATIONS, defaultConsultations);
};

export const saveErpClinicalConsultations = (consultations: ClinicalConsultation[]): void => {
  safeSet(STORAGE_KEYS.CONSULTATIONS, consultations);
};

export const loadErpClinicalVaccinations = (defaultVaccinations: ClinicalVaccination[] = []): ClinicalVaccination[] => {
  return safeGet<ClinicalVaccination[]>(STORAGE_KEYS.VACCINATIONS, defaultVaccinations);
};

export const saveErpClinicalVaccinations = (vaccinations: ClinicalVaccination[]): void => {
  safeSet(STORAGE_KEYS.VACCINATIONS, vaccinations);
};

export const loadErpClinicalExams = (defaultExams: ClinicalExamVault[] = []): ClinicalExamVault[] => {
  return safeGet<ClinicalExamVault[]>(STORAGE_KEYS.EXAMS, defaultExams);
};

export const saveErpClinicalExams = (exams: ClinicalExamVault[]): void => {
  safeSet(STORAGE_KEYS.EXAMS, exams);
};
